export interface Point {
  x: number;
  y: number;
}

/**
 * Generate a cumulative distance map for a list of points.
 * The first entry is always 0 and each subsequent value is the
 * accumulated Euclidean distance up to that point.
 */
function distanceMap(points: Point[]): number[] {
  const map: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    map.push(map[i - 1] + Math.hypot(dx, dy));
  }
  return map;
}

/**
 * Resample a polyline to a fixed number of points using the provided
 * distance map so that samples are evenly spaced by arc length.
 */
export function resampleByDistance(points: Point[], samples: number): Point[] {
  if (points.length === 0) return [];
  if (points.length === 1 || samples <= 1) return [points[0]];

  const map = distanceMap(points);
  const total = map[map.length - 1];
  const step = total / (samples - 1);
  const result: Point[] = [];

  let target = 0;
  let idx = 0;
  for (let i = 0; i < samples; i++) {
    while (idx < map.length - 2 && target > map[idx + 1]) idx++;
    const t = (target - map[idx]) / (map[idx + 1] - map[idx] || 1);
    const p0 = points[idx];
    const p1 = points[idx + 1];
    result.push({ x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t });
    target += step;
  }

  return result;
}

